import React from 'react';

import { Link } from '~/lib/react-router-dom';
import dayjs from '~/timeInit';

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';

import CopyableField from '~/components/Common/CopyableField';

import type { ManualAccrual } from '~/types/loyaltyContract';

interface ManualAccrualDetailDialogProps {
  open: boolean;
  onClose: () => void;
  accrual: ManualAccrual | null;
  onRetry?: () => void;
}

export default function ManualAccrualDetailDialog({ open, onClose, accrual, onRetry }: ManualAccrualDetailDialogProps) {
  if (!accrual) {
    return null;
  }

  const getStatusColor = (status: string): 'warning' | 'success' | 'error' => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'PROCESSED':
        return 'success';
      case 'FAILED':
        return 'error';
      default:
        return 'warning';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h6">Manual Accrual Details</Typography>
          <Chip label={accrual.manualAccrualStatus} color={getStatusColor(accrual.manualAccrualStatus)} size="small" />
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} mt={1}>
          {/* Basic Information */}
          <Box>
            <Typography variant="subtitle2" gutterBottom color="primary">
              Basic Information
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: '200px' }}>Accrual ID</TableCell>
                  <TableCell>
                    <CopyableField
                      value={accrual.id}
                      tooltip={`Copy accrual ID: ${accrual.id}`}
                      sx={{ display: 'flex', alignItems: 'center', gap: '4px', '& .MuiSvgIcon-root': { fontSize: 20 } }}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Customer ID</TableCell>
                  <TableCell>
                    <CopyableField
                      value={accrual.customerId}
                      tooltip={`Copy customer ID: ${accrual.customerId}`}
                      sx={{ display: 'flex', alignItems: 'center', gap: '4px', '& .MuiSvgIcon-root': { fontSize: 20 } }}
                      label={
                        <Link
                          to={`/users/${accrual.customerId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'underline' }}
                        >
                          {accrual.customerId}
                        </Link>
                      }
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Order ID</TableCell>
                  <TableCell>
                    <CopyableField
                      value={accrual.orderId}
                      tooltip={`Copy order ID: ${accrual.orderId}`}
                      sx={{ display: 'flex', alignItems: 'center', gap: '4px', '& .MuiSvgIcon-root': { fontSize: 20 } }}
                      label={
                        <Link
                          to={`/purchases/${accrual.orderId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'underline' }}
                        >
                          {accrual.orderId}
                        </Link>
                      }
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Item ID</TableCell>
                  <TableCell>
                    <CopyableField
                      value={accrual.itemId}
                      tooltip={`Copy item ID: ${accrual.itemId}`}
                      sx={{ display: 'flex', alignItems: 'center', gap: '4px', '& .MuiSvgIcon-root': { fontSize: 20 } }}
                      label={
                        <Link
                          to={`/purchases/${accrual.orderId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'underline' }}
                        >
                          {accrual.itemId}
                        </Link>
                      }
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Product Type</TableCell>
                  <TableCell>{accrual.productType}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          <Divider />

          {/* Points Breakdown */}
          <Box>
            <Typography variant="subtitle2" gutterBottom color="primary">
              Points Breakdown
            </Typography>
            <Grid container spacing={3}>
              {/* Estimated Points */}
              <Grid size={{ xs: 6 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Estimated (from calculation)
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Base Points</TableCell>
                      <TableCell align="right">{accrual.pointsFromEstimate}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Bonus Points</TableCell>
                      <TableCell align="right">{accrual.bonusPointsFromEstimate}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Status Credits</TableCell>
                      <TableCell align="right">{accrual.statusCreditsFromEstimate}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Grid>

              {/* Pending Points */}
              <Grid size={{ xs: 6 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Pending (actual system data)
                </Typography>
                {accrual.pendingPoints ? (
                  <>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Base Points</TableCell>
                          <TableCell align="right">{accrual.pendingPoints.points}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Bonus Points</TableCell>
                          <TableCell align="right">{accrual.pendingPoints.bonusPoints}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Status Credits</TableCell>
                          <TableCell align="right">{accrual.pendingPoints.statusCredits}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">
                            <Chip label={accrual.pendingPoints.status} size="small" variant="outlined" />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>

                    {accrual.pendingPoints.customRulesApplied &&
                      accrual.pendingPoints.customRulesApplied.length > 0 && (
                        <Box mt={2}>
                          <Typography variant="body2" fontWeight="bold" gutterBottom>
                            Custom Rules Applied
                          </Typography>
                          <Stack spacing={0.5}>
                            {accrual.pendingPoints.customRulesApplied.map((rule) => (
                              <Chip key={rule.id} label={rule.name} size="small" color="primary" variant="outlined" />
                            ))}
                          </Stack>
                        </Box>
                      )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No pending points data available
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Additional Details */}
          <Box>
            <Typography variant="subtitle2" gutterBottom color="primary">
              Additional Details
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: '200px' }}>Contract Price</TableCell>
                  <TableCell>${accrual.contractPrice.toFixed(2)}</TableCell>
                </TableRow>
                {accrual.marginPackage !== undefined && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Margin Package</TableCell>
                    <TableCell>{accrual.marginPackage}</TableCell>
                  </TableRow>
                )}
                {accrual.bonusMechanic !== undefined && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Bonus Mechanic</TableCell>
                    <TableCell>{accrual.bonusMechanic}</TableCell>
                  </TableRow>
                )}
                {accrual.numberOfNights !== undefined && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Number of Nights</TableCell>
                    <TableCell>{accrual.numberOfNights}</TableCell>
                  </TableRow>
                )}
                {accrual.offerId && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Offer ID</TableCell>
                    <TableCell>{accrual.offerId}</TableCell>
                  </TableRow>
                )}
                {accrual.packageId && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Package ID</TableCell>
                    <TableCell>
                      <CopyableField
                        value={accrual.packageId}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          '& .MuiSvgIcon-root': { fontSize: 20 },
                        }}
                        tooltip={`Copy package ID: ${accrual.packageId}`}
                      />
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Booking Date</TableCell>
                  <TableCell>{dayjs(accrual.bookingDate).format('DD/MM/YYYY')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Points Earn Date</TableCell>
                  <TableCell>{dayjs(accrual.pointsEarnDate).format('DD/MM/YYYY')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Created At</TableCell>
                  <TableCell>{dayjs(accrual.createdAt).format('DD/MM/YYYY HH:mm:ss')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Updated At</TableCell>
                  <TableCell>{dayjs(accrual.updatedAt).format('DD/MM/YYYY HH:mm:ss')}</TableCell>
                </TableRow>
                {accrual.processedAt && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Processed At</TableCell>
                    <TableCell>{dayjs(accrual.processedAt).format('DD/MM/YYYY HH:mm:ss')}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>

          {/* Error Message */}
          {accrual.errorMessage && (
            <>
              <Divider />
              <Box>
                <CopyableField
                  value={accrual.errorMessage}
                  sx={{ display: 'flex', alignItems: 'center', gap: '4px', '& .MuiSvgIcon-root': { fontSize: 20 } }}
                  label={
                    <Typography variant="subtitle2" color="primary" component="span">
                      Error Message
                    </Typography>
                  }
                  tooltip="Copy error message"
                />
                <Box
                  sx={{
                    p: 2,
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2">{accrual.errorMessage}</Typography>
                </Box>
              </Box>
            </>
          )}

          {/* Payload (Technical) */}
          {accrual.payload && (
            <>
              <Divider />
              <Box>
                <CopyableField
                  value={JSON.stringify(accrual.payload, null, 2)}
                  sx={{ display: 'flex', alignItems: 'center', gap: '4px', '& .MuiSvgIcon-root': { fontSize: 20 } }}
                  label={
                    <Typography variant="subtitle2" color="primary" component="span">
                      Technical Payload
                    </Typography>
                  }
                  tooltip="Copy technical payload"
                />
                <Box
                  sx={{
                    p: 2,
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    maxHeight: '200px',
                    overflow: 'auto',
                  }}
                >
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {JSON.stringify(accrual.payload, null, 2)}
                  </pre>
                </Box>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {onRetry && (
          <Button onClick={onRetry} variant="contained" color="primary">
            Retry This Accrual
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
